import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { OneAndManyExplainer } from "@/components/sutra/OneAndManyExplainer";
import { SutraMark } from "@/components/sutra/SutraMark";
import { WaitlistForm } from "@/components/sutra/WaitlistForm";
import { FriendToolkit } from "@/components/sutra/FriendToolkit";
import { PracticeCard } from "@/components/sutra/PracticeCard";

/**
 * Sutra — single-page MVP. This file scaffolds the landing structure:
 * sticky nav, hero, "you're not alone" quotes, How it works, the four
 * content sections (Explainer / Toolkit / Practice / Waitlist — these
 * are intentionally empty placeholders to be filled in next steps),
 * and the footer disclaimer.
 *
 * All copy is real (from the product brief); no lorem ipsum.
 */

const NAV_ITEMS = [
  { href: "#explainer", label: "Explainer" },
  { href: "#toolkit", label: "Toolkit" },
  { href: "#practice", label: "Practice" },
  { href: "#waitlist", label: "Waitlist" },
];

const QUOTES = [
  "I've read the starter pack and I'm still just kind of lost.",
  "Growing up Hindu, no one explained what it actually means.",
  "My friend asked why we have so many gods and I froze.",
];

const STEPS = [
  {
    n: "01",
    title: "Hear it",
    body: "Plain-language explainers for the parts that always seemed confusing.",
  },
  {
    n: "02",
    title: "Reflect on it",
    body: "Questions that turn an idea into something that's actually yours.",
  },
  {
    n: "03",
    title: "Feel it",
    body: "Tiny, optional practices — two minutes, no belief required.",
  },
];

const Sutra = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground paper-grain">
      <Helmet>
        <title>Sutra — Hinduism, explained like a friend would</title>
        <meta
          name="description"
          content="A calm, guided path into Hindu dharma — for anyone who's ever felt lost, overwhelmed, or put on the spot."
        />
      </Helmet>

      {/* ───────── Sticky nav ───────── */}
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "border-b border-border/60 bg-background/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <a
            href="#top"
            className="group inline-flex items-center px-1"
            aria-label="Sutra — home"
          >
            <img
              src="/sutra_logo.svg"
              alt="Sutra"
              width={113}
              height={32}
              className="h-8 w-auto select-none"
              draggable={false}
            />
          </a>
          <ul className="hidden items-center gap-7 text-sm text-ink-soft md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className="transition-colors hover:text-primary"
                >
                  {item.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href="#waitlist"
                className="rounded-full bg-foreground px-4 py-1.5 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
              >
                Join
              </a>
            </li>
          </ul>
          <a
            href="#waitlist"
            className="rounded-full bg-foreground px-3.5 py-1.5 text-xs font-medium text-background md:hidden"
          >
            Join
          </a>
        </nav>
      </header>

      <main id="top">
        {/* ───────── Hero ───────── */}
        <section className="relative mx-auto max-w-3xl px-5 pb-16 pt-14 sm:px-8 sm:pt-24 sm:pb-24">
          {/* Soft ॐ motif — quiet, warm, not clip-art. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-6 right-0 select-none font-display text-[11rem] leading-none text-terracotta/[0.08] sm:-top-10 sm:right-4 sm:text-[16rem]"
          >
            ॐ
          </div>

          <p className="relative mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            A gentle introduction
          </p>

          <h1 className="relative font-display text-[2.4rem] font-light leading-[1.05] tracking-tight text-foreground sm:text-6xl md:text-[4.25rem]">
            Hinduism,{" "}
            <span className="italic text-primary">explained like a friend</span>{" "}
            would.
          </h1>

          <p className="relative mt-7 max-w-xl font-body text-lg leading-relaxed text-ink-soft sm:text-xl">
            A calm, guided path into Hindu dharma — for anyone who's ever
            felt lost, overwhelmed, or put on the spot.
          </p>

          <div className="relative mt-9 flex flex-wrap items-center gap-3">
            <a
              href="#explainer"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Try the explainer
              <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </a>
            <a
              href="#waitlist"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium text-foreground transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Join the waitlist
            </a>
          </div>

          <p className="relative mt-8 text-xs text-muted-foreground">
            Made for the half-in, the diaspora, the quietly overwhelmed,
            and the genuinely curious.
          </p>
        </section>

        <Divider />

        {/* ───────── "You're not alone" quotes ───────── */}
        <section className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-28">
          <p className="mb-10 text-center text-[11px] uppercase tracking-[0.22em] text-ink-soft">
            You're not alone
          </p>
          <div className="grid gap-5 sm:grid-cols-3">
            {QUOTES.map((q, i) => (
              <figure
                key={i}
                className="relative rounded-2xl border border-border bg-card/80 p-6 shadow-[0_1px_0_hsl(var(--accent)/0.15)_inset,0_8px_24px_-18px_hsl(24_25%_15%/0.25)]"
              >
                <span
                  aria-hidden
                  className="absolute -top-2 left-5 font-display text-3xl leading-none text-accent/70"
                >
                  &ldquo;
                </span>
                <blockquote className="font-display text-[1.05rem] italic leading-snug text-foreground">
                  {q}
                </blockquote>
              </figure>
            ))}
          </div>
        </section>

        <Divider />

        {/* ───────── How it works ───────── */}
        <section className="mx-auto max-w-4xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="mb-12 text-center">
            <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
              How it works
            </p>
            <h2 className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl">
              Hear it. Reflect on it. Feel it.
            </h2>
          </div>
          <ol className="grid gap-6 sm:grid-cols-3">
            {STEPS.map((step) => (
              <li
                key={step.n}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="mb-4 font-display text-sm tracking-[0.2em] text-accent">
                  {step.n}
                </div>
                <h3 className="mb-2 font-display text-xl font-medium text-foreground">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-ink-soft">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <Divider />

        {/* ───────── Placeholder sections (to be built in next steps) ───────── */}
        <section
          id="explainer"
          className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28"
        >
          <div className="mb-10">
            <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
              The hero feature
            </p>
            <h2 className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl">
              One God… but thousands of gods?
            </h2>
            <p className="mt-3 text-sm text-ink-soft">
              A five-step walkthrough. Take your time — there's no test.
            </p>
          </div>
          <OneAndManyExplainer />
        </section>
        <section
          id="toolkit"
          className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-24"
          aria-labelledby="toolkit-heading"
        >
          <div className="mx-auto max-w-3xl">
            <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
              The friend toolkit
            </p>
            <h2
              id="toolkit-heading"
              className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl"
            >
              When a friend asks…
            </h2>
            <p className="mt-3 max-w-xl text-sm text-ink-soft">
              Tap a card to reveal a short, confident answer. Copy it,
              or share a clean card to send in a text.
            </p>
          </div>
          <div className="mt-10">
            <FriendToolkit />
          </div>
        </section>
        <section
          id="practice"
          className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24"
          aria-labelledby="practice-heading"
        >
          <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
            A taste of practice
          </p>
          <h2
            id="practice-heading"
            className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl"
          >
            Don't just read it — try it.
          </h2>
          <p className="mt-3 max-w-xl text-sm text-ink-soft">
            One short, belief-optional inquiry. Two minutes, at your own pace.
          </p>
          <div className="mt-10">
            <PracticeCard />
          </div>
        </section>
        <section
          id="waitlist"
          className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24"
        >
          <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
            Join the waitlist
          </p>
          <h2 className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl">
            Want the full guided path when it's ready?
          </h2>
          <p className="mt-4 max-w-xl text-sm text-ink-soft">
            We'll only write when there's something quiet and worth your
            time. No launch hype, no countdowns.
          </p>
          <div className="mt-8 max-w-xl">
            <WaitlistForm source="waitlist-section" />
          </div>
        </section>

        {/* ───────── Footer ───────── */}
        <footer className="border-t border-border/60 bg-paper-warm/40">
          <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-12">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-medium text-foreground">
                  <SutraMark idSuffix="footer" className="h-6 w-6" />
                  <span>Sutra</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  A calm introduction to Hindu dharma.
                </p>
              </div>
              <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
                Sutra is in early development. All content is illustrative
                and reviewed for accuracy and neutrality before release.
                Apolitical and sect-neutral by design.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

const Divider = () => (
  <div className="mx-auto max-w-3xl px-5 sm:px-8">
    <div className="editorial-rule" />
  </div>
);

interface PlaceholderProps {
  id: string;
  kicker: string;
  title: string;
  note: string;
}

const PlaceholderSection = ({ id, kicker, title, note }: PlaceholderProps) => (
  <section
    id={id}
    className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24"
    aria-label={`${title} (coming soon)`}
  >
    <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft">
      {kicker}
    </p>
    <h2 className="font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl">
      {title}
    </h2>
    <p className="mt-4 text-sm italic text-muted-foreground">{note}</p>
  </section>
);

export default Sutra;
